import NetInfo from '@react-native-community/netinfo'
import React, { createContext, useContext, useEffect, useState } from 'react'
import theme from '../../../../theme'
import { useNavigateTabsStore } from '../../../redux/zustand/navigateTabsStore'
import { useTabStore } from '../../../redux/zustand/tabsStore'
import { getAlert } from './AlertsContext'
import { UserServices } from '../../../../database/user.database'
import { useFooterApp } from '../../../redux/zustand/footerAppStore'
import { Platform } from 'react-native'
import { useBottomSheet } from '../../customUtils/context/BottomSheetContext'
// RightSidebar solo existe en web; proteger el import/uso
let useRightSidebarSafe = null
try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    useRightSidebarSafe = require('../../web/context/RightSidebarContext').useRightSidebar
} catch (e) {
    // no-op fuera de web
}

const ThemeContext = createContext()

export const COLOR_THEME_HEADER = [
    {
        route: 'BenefitsHome',
        color: theme.colors.colorPrimary,
    },
    {
        route: 'Notifications',
        color: 'white',
    },
    {
        route: 'PostsList',
        color: 'white',
    },
    {
        route: 'Portfolio',
        color: 'white',
    },
]

const DEFAULT_COLOR = 'white'

export const find_color = (route) => {
    const match = COLOR_THEME_HEADER.find((obj) => obj.route === route)

    if (match) {
        return match.color
    } else {
        return DEFAULT_COLOR
    }
}

export const ThemeProvider = ({ children }) => {
    const [backgroundColor, setBackgroundColor] = useState(DEFAULT_COLOR) // Color de fondo inicial

    const setAppBackgroundColor = (color) => {
        setBackgroundColor(color)
    }

    const setResetBackgroundColor = () => {
        setBackgroundColor(DEFAULT_COLOR)
    }

    const value = {
        backgroundColor,
        setAppBackgroundColor,
        setResetBackgroundColor,
    }

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
    return useContext(ThemeContext)
}

const ConnectionStatePro = createContext()

export const THEME_COLOR_ALERT = {
    SUCCESS: 'rgba(0, 158, 77, 0.8)',
    ERROR: 'rgba(229, 29, 13, 0.8)',
}

export const ConnectionState = ({ children }) => {
    const [isConnected, setConnected] = useState(true)
    const [hasInternet, setHasInternet] = useState(true)
    const { setAlert } = getAlert()
    const [wasPreviouslyConnected, setWasPreviouslyConnected] = useState(null)
    // Estado derivado estable con periodo de gracia para evitar falsos positivos
    const [stableConnected, setStableConnected] = useState(true)
    const offlineTimerRef = React.useRef(null)
    const onlineTimerRef = React.useRef(null)

    useEffect(() => {
        // Mostrar alertas solo cuando el estado estable cambia
        if (wasPreviouslyConnected === null) {
            setWasPreviouslyConnected(stableConnected)
            return
        }
        if (wasPreviouslyConnected === false && stableConnected === true) {
            setAlert('¡Conexión restablecida!', 'white', 'wifi', THEME_COLOR_ALERT.SUCCESS)
        } else if (wasPreviouslyConnected === true && stableConnected === false) {
            setAlert('No tienes conexión', 'white', 'wifi-off', THEME_COLOR_ALERT.ERROR)
        }
        setWasPreviouslyConnected(stableConnected)
    }, [stableConnected])

    useEffect(() => {
        const unsubscribe = NetInfo.addEventListener((state) => {
            // Tratar isInternetReachable null/undefined como "desconocido" (no asumimos falso)
            const nextIsConnected = state.isConnected === true
            const reach = state.isInternetReachable
            const nextHasInternet = reach === false ? false : true

            // Actualizar flags crudos (para quienes los lean), pero con mapping seguro
            setIsConnected(nextIsConnected)
            setHasInternet(nextHasInternet)

            // Determinar candidato a conectado/desconectado
            const candidateConnected = nextIsConnected && nextHasInternet

            // Aplicar periodo de gracia con timers
            if (candidateConnected) {
                // Cancelar cualquier intento de marcar offline
                if (offlineTimerRef.current) {
                    clearTimeout(offlineTimerRef.current)
                    offlineTimerRef.current = null
                }
                // Evitar flicker al reconectar: esperar un breve tiempo estable
                if (!stableConnected) {
                    if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
                    onlineTimerRef.current = setTimeout(() => {
                        setStableConnected(true)
                        onlineTimerRef.current = null
                    }, 300)
                } else {
                    // Ya estamos online de forma estable
                    if (onlineTimerRef.current) {
                        clearTimeout(onlineTimerRef.current)
                        onlineTimerRef.current = null
                    }
                }
            } else {
                // Posible desconexión: aplicar gracia para evitar falsos negativos temporales
                if (onlineTimerRef.current) {
                    clearTimeout(onlineTimerRef.current)
                    onlineTimerRef.current = null
                }
                if (!offlineTimerRef.current) {
                    offlineTimerRef.current = setTimeout(() => {
                        setStableConnected(false)
                        offlineTimerRef.current = null
                    }, 1200) // 1.2s de gracia antes de mostrar "sin conexión"
                }
            }
        })

        return () => {
            unsubscribe()
            if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current)
            if (onlineTimerRef.current) clearTimeout(onlineTimerRef.current)
        }
    }, [])

    const setIsConnected = (value) => {
        setConnected(value)
    }

    const value = {
        setIsConnected,
        isConnected,
        hasInternet,
        // Exponer estado efectivo estable por si algún consumidor lo requiere
        connectedStable: stableConnected,
    }

    return <ConnectionStatePro.Provider value={value}>{children}</ConnectionStatePro.Provider>
}

export const getConection = () => {
    return useContext(ConnectionStatePro)
}

const ButtonDownloadContext = createContext()

export const ButtonDownloadContextProvider = ({ children }) => {
    const [linkDownload, setLinkDownload] = useState('')
    const [isLinkDownload, setIsLinkDownload] = useState(false)

    const value = {
        linkDownload,
        setLinkDownload,
        setIsLinkDownload,
        isLinkDownload,
    }

    return <ButtonDownloadContext.Provider value={value}>{children}</ButtonDownloadContext.Provider>
}

export const useButtonDownloadContext = () => {
    const context = useContext(ButtonDownloadContext)
    if (!context) {
        throw new Error('useButtonDownloadContext must be used within a ButtonDownloadContextProvider')
    }
    return context
}
//Este Provider se podría usar para valores x sin necesidad de estar creando siempre alguno
const DataStatePro = createContext()

export const DataState = ({ children }) => {
    const [footerRoute, setFooterRoute] = useState('PostsList')
    const [prevFooterRoute, setPrevFooterRoute] = useState('')
    const [routeStack, setRouteStack] = useState(['PostsList'])
    const [showFooter, setShowFooter] = useState(true)
    const [customBack, setCustomBack] = useState(false)
    const [navigationParams, setNavigationParams] = useState({})
    const tagsDis = useTabStore((state) => state.tagsDis)
    const setIndexTab = useNavigateTabsStore((state) => state.setIndexTab)
    const [isVisibleTabBar, setIsVisibleTabBar] = useState(true)

    const footer_app = useFooterApp((state) => state.footer_app)
    // Flags globales de overlays/widgets
    const { isOpen: isBottomSheetOpen } = useBottomSheet()
    let isRightSidebarOpen = false
    if (Platform.OS === 'web' && typeof useRightSidebarSafe === 'function') {
        try {
            const { activeRightSidebar } = useRightSidebarSafe()
            isRightSidebarOpen = !!activeRightSidebar
        } catch (e) {
            isRightSidebarOpen = false
        }
    }

    const setCurrentFooterRoute = (value, params = {}) => {
        console.log('[DataState] setCurrentFooterRoute:', value, 'params:', params)
        setNavigationParams(params)
        if (validateCurrentFooterRoute(value)) {
            const tabs = tagsDis
            setIndexTab(tabs && tabs.length > 0 ? tabs[0].index : 1)
        }

        const index = footer_app && footer_app.length > 0 ? footer_app.findIndex((item) => item.label === value) : -1

        if (index !== -1) {
            // la view pertenece al footer
            setIsVisibleTabBar(true)
        } else {
            setIsVisibleTabBar(false)
        }
        if (footerRoute !== value) {
            // Actualizar prev y apilar nueva ruta, evitando duplicados consecutivos
            setPrevFooterRoute(footerRoute)
            setRouteStack((prev) => {
                const last = prev[prev.length - 1]
                if (last === value) return prev
                return [...prev, value]
            })
            setFooterRoute(value)
        }
    }

    // Back interno: vuelve a la ruta anterior en el stack sin apilar nuevamente
    const goBackInternal = () => {
        setRouteStack((prev) => {
            if (!prev || prev.length <= 1) {
                // No hay historial, permanecer en la actual
                return prev || ['PostsList']
            }
            const newStack = prev.slice(0, -1)
            const target = newStack[newStack.length - 1] || 'PostsList'

            // Actualizar flags de footer y prev
            const index = footer_app && footer_app.length > 0 ? footer_app.findIndex((item) => item.label === target) : -1
            setIsVisibleTabBar(index !== -1)
            setPrevFooterRoute(footerRoute)
            setFooterRoute(target)

            // Limpiar params de navegación al volver
            setNavigationParams({})
            return newStack
        })
    }

    const validateCurrentFooterRoute = (value) => {
        const map = {
            ScheduleDistributor: false,
            AddStores: false,
            StoreDetail: false,
            ExecutionView: false,
        }

        return map.hasOwnProperty(value) ? map[value] : true
    }
    const calledCustomBack = () => {
        setCustomBack(!customBack)
    }

    const value = {
        footerRoute,
        setCurrentFooterRoute,
        isVisibleTabBar,
        prevFooterRoute,
        routeStack,
        goBackInternal,
        customBack,
        calledCustomBack,
        showFooter,
        navigationParams,
        setNavigationParams,
        // Exponer flags para navegación web/back
        isBottomSheetOpen: !!isBottomSheetOpen,
        isRightSidebarOpen: !!isRightSidebarOpen,
    }
    return <DataStatePro.Provider value={value}>{children}</DataStatePro.Provider>
}

export const getData = () => {
    return useContext(DataStatePro)
}
